import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { Api } from '@api/api';
import { getAnnouncement } from '@api/functions';
import { AnnouncementDto } from '@api/models';
import { DeviceService } from '../../services/device-detector/device.service';
import { ToolbarService } from '../../services/toolbar.service';
import { WindowResizeService } from '../../services/window-resize.service';
import { onWindowLoad } from '../../helpers/on-window-load.function';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { resolveHttpErrorMessage } from '@/helpers/resolve-http-error-message.helper';

@Component({
  selector: 'announcement',
  templateUrl: './announcement.component.html',
  styleUrls: ['./announcement.component.scss'],
  imports: [],
  host: {
    '[class.fixed]': 'announcement()?.isFixed',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnnouncementComponent implements OnInit {
  private api = inject(Api);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private deviceService = inject(DeviceService);
  private toolbarService = inject(ToolbarService);
  private windowResizeService = inject(WindowResizeService);

  announcement = signal<AnnouncementDto | undefined>(undefined);
  fetchError = signal<string | null>(null);
  type = signal<'winterHolidays' | 'springHolidays' | 'attention' | undefined>(undefined);

  readonly announcementEl = viewChild('announcementRef', { read: ElementRef });

  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.fetchAnnouncement();
    this.handleWindowResize();

    onWindowLoad(() => this.fetchAnnouncement());
  }

  private fetchAnnouncement() {
    this.api.invoke(getAnnouncement)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: response => {
          this.announcement.set(response);

          if (!this.deviceService.isPlatformBrowser()) { return; }

          if (response?.isFixed) {
            this.changeDetectorRef.detectChanges();
          }

          this.updateSavedElHeight();
        },
        error: err => this.fetchError.set(resolveHttpErrorMessage(err)),
      });
  }

  private handleWindowResize() {
    this.windowResizeService.resized$
      .pipe( takeUntilDestroyed(this.destroyRef) )
      .subscribe(() => this.updateSavedElHeight());
  }

  /**
   * Zero unless it is fixed: one that scrolls away with the page covers nothing. A fixed one
   * is sticky rather than `position: fixed`, so it keeps its place in the page's flow from the
   * server-rendered HTML on, and nothing has to be pushed down under it once this runs.
   */
  private updateSavedElHeight(): void {
    const el: HTMLElement | undefined = this.announcementEl()?.nativeElement;
    const height = this.announcement()?.isFixed && el ? el.getBoundingClientRect().height : 0;
    this.toolbarService.announcementElHeight.set(height);
  }
}
